import {
  PostPartnershipCompaniesRequestBody,
  PutPartnershipCompaniesRequestBody,
} from '@/src/apis/rest/partnershipCompaines.dtos';
import { postPartnershipCompaniesAPI } from '@/src/apis/rest/partnershipCompanies';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { toast } from '@/src/hooks/useToast';
import { areaCodeRegex, emailRegex, urlRegex } from '@/src/utils/regex';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface PartnershipCompaniesCreateModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOk: (id: number) => void;
}

const formSchema = () => {
  return z.object({
    name: z.string().min(1),
    phone: z.string().regex(areaCodeRegex),
    email: z.string().regex(emailRegex).optional().or(z.literal('')),
    homepageUrl: z.string().regex(urlRegex).optional().or(z.literal('')),
    isDefault: z.boolean(),
    isUsing: z.boolean(),
  });
};

const DEFAULT_VALUE = {
  name: '',
  phone: '',
  isDefault: false,
  isUsing: true,
};

const PartnershipCompaniesCreateModal = ({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOk,
}: PartnershipCompaniesCreateModalProps) => {
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({ type: DialogType.ALERT });
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const [isHomepageUrlError, setIsHomepageUrlError] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<PutPartnershipCompaniesRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUE,
    reValidateMode: 'onSubmit',
  });

  const postPartnershipCompaniesMutation = useMutation({
    mutationFn: postPartnershipCompaniesAPI,
  });

  const handleCloseModal = () => {
    if (form.formState.isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };

  const handleSubmit = (values: PostPartnershipCompaniesRequestBody) => {
    postPartnershipCompaniesMutation.mutate(values, {
      onSuccess: response => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        onOpenChangeProps(false);
        onOk(response.id);
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };

  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={!form.formState.isValid}
          onClick={openSaveDialog}
          isLoading={postPartnershipCompaniesMutation.isPending}
        >
          등록
        </Button>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[697px]',
        }}
        title="제휴사 정보 등록"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="제휴사 명" isRequired error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="제휴사 명을 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="대표 전화번호" isRequired error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="대표 전화번호를 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="대표 이메일" error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="대표 이메일을 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="homepageUrl"
              render={({ field }) => {
                return (
                  <FormContent
                    label="홈페이지 링크"
                    error={
                      isHomepageUrlError
                        ? {
                            type: 'pattern',
                            message:
                              'url 형식을 확인해주세요 (https://example.com)',
                          }
                        : undefined
                    }
                  >
                    <Input
                      value={field?.value}
                      placeholder="제휴사의 홈페이지 링크를 입력해주세요"
                      onChange={e => {
                        field.onChange(e.target.value || undefined);

                        if (e.target.value) {
                          setIsHomepageUrlError(!urlRegex.test(e.target.value));
                        } else {
                          setIsHomepageUrlError(false);
                        }
                      }}
                      onClear={() => {
                        setIsHomepageUrlError(false);
                        field.onChange('');
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="isUsing"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="사용 여부" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: '사용', value: 'true' },
                        { label: '미사용', value: 'false' },
                      ]}
                      rootProps={{
                        value: field.value ? 'true' : 'false',
                        onValueChange: (e: string) => {
                          field.onChange(e === 'true');
                        },
                      }}
                    />
                  </FormContent>
                );
              }}
            />
          </form>
        </Form>
        <BlockAlertDialog
          open={isBlockAlertOpen}
          onOpenChange={closeBlockAlertDialog}
          onCancel={() => {
            closeBlockAlertDialog();
          }}
          onOk={handleBlockModalOk}
          title="취소"
        >
          변경사항은 저장되지 않습니다. 취소하시겠습니까?
        </BlockAlertDialog>
      </Dialog>

      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={postPartnershipCompaniesMutation.isPending}
        />
      )}
    </>
  );
};

export default PartnershipCompaniesCreateModal;
